import { useMemo, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import TagRoundedIcon from '@mui/icons-material/TagRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import type { Channel } from '../../../../types/chat'
import { useNls } from '../../../../nls/nlsProvider'
import { Button } from '../../../Common/Button'
import { Pressable } from '../../../Common/Pressable'
import { TextInput } from '../../../Common'
import { MobileSheet } from '../MobileSheet'

type Props = {
  open: boolean
  onClose: () => void
  channels: Channel[]
  activeChannelId: string | null
  unreadByChannel: Record<string, boolean>
  onSelect: (channelId: string) => void
  onCreateChannel?: () => void
  workspaceName: string
}

export const ChannelSwitcherSheet = ({
  open,
  onClose,
  channels,
  activeChannelId,
  unreadByChannel,
  onSelect,
  onCreateChannel,
  workspaceName,
}: Props) => {
  const { t } = useNls()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return channels
    return channels.filter((channel) => (channel.name + ' ' + (channel.slug ?? '')).toLowerCase().includes(needle))
  }, [channels, query])

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title={t('NLSChatSheetChannelsTitle')}
      hint={workspaceName}
      ariaLabel={t('NLSChatSheetChannelsTitle')}
      testId="member-chat-channels-sheet"
      footer={onCreateChannel ? (
        <Button
          size="lg"
          variant="primary"
          className="w-full"
          icon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => { onCreateChannel(); onClose() }}
        >
          {t('NLSChatActionCreateChannel')}
        </Button>
      ) : null}
    >
      <TextInput
        value={query}
        onChange={setQuery}
        placeholder={t('NLSChatChannelFilterPlaceholder')}
        aria-label={t('NLSChatChannelFilterPlaceholder')}
        leadingIcon={<SearchRoundedIcon sx={{ fontSize: 16, color: 'var(--muted)' }} />}
      />

      {filtered.length === 0 ? (
        <p style={{ padding: '20px 8px', color: 'var(--muted)', fontSize: 12, fontStyle: 'italic', margin: 0 }}>
          {query.trim().length > 0 ? t('NLSChatNoChannelMatches') : t('NLSChatEmptyNoChannels')}
        </p>
      ) : (
        <div role="listbox" aria-label={t('NLSChatSheetChannelsTitle')}>
          {filtered.map((channel) => {
            const isActive = channel.id === activeChannelId
            const hasUnread = !isActive && !!unreadByChannel[channel.id]
            return (
              <Pressable
                key={channel.id}
                role="option"
                aria-selected={isActive}
                className={`memberChatRow ${isActive ? 'isActive' : ''}`}
                onClick={() => { onSelect(channel.id); onClose() }}
              >
                <span className="memberChatRow__ic" aria-hidden="true">
                  {channel.type === 'private'
                    ? <LockRoundedIcon sx={{ fontSize: 14 }} />
                    : <TagRoundedIcon sx={{ fontSize: 14 }} />}
                </span>
                <span className="memberChatRow__body">
                  <span className="memberChatRow__name">{channel.name}</span>
                  {channel.slug && channel.slug !== channel.name ? (
                    <span className="memberChatRow__preview">#{channel.slug}</span>
                  ) : null}
                </span>
                {hasUnread ? <span className="memberChatRow__badge" aria-label={t('NLSChatUnreadLabel')}>•</span> : null}
              </Pressable>
            )
          })}
        </div>
      )}
    </MobileSheet>
  )
}
